import React, { useState } from "react";
import ReactDatePicker, { registerLocale, setDefaultLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useIntl } from "react-intl";
import "./datePicker.less";
import { Icon_Calendar } from "../Icons/icons";
import CustomHeader from "./customHeader/customHeader";
import moment, { Moment, isMoment, isDate } from "moment";
import pl from "date-fns/locale/pl";
// Register the locale
registerLocale("pl-PL", pl);
// Set the default locale
setDefaultLocale("pl-PL");

interface DatePickerProps {
   onChange?: (date: Date | Moment | string) => void;
   onStartEdit?: () => void;
   defaultValue?: Date | Moment | null | undefined;
   disabled?: boolean;
   readOnly?: boolean;
   className?: string;
   format?: string;
   showTime?: boolean;
   showTimeOnly?: boolean;
   size?: "small" | "middle" | "large";
   placeholder?: string;
   timeIntervals?: number;
   style?: React.CSSProperties;
   onOpenChange?: () => void;
   onClickOutside?: () => void;
   isOpen?: boolean;
   icon?: React.ReactElement;
   inline?: boolean;
   minTime?: any;
   maxTime?: any;
   minDate?: Date;
   maxDate?: Date;
   value?: Date | Moment | String;
   wrapperClassName?: string;
}

export const DatePicker: React.FC<DatePickerProps> = ({
   onChange,
   onStartEdit,
   defaultValue,
   disabled,
   readOnly,
   className,
   format,
   showTime,
   showTimeOnly = false,
   size,
   placeholder,
   timeIntervals,
   style,
   onOpenChange,
   onClickOutside,
   isOpen,
   icon,
   inline = false,
   minTime,
   maxTime,
   minDate,
   maxDate,
   value,
   wrapperClassName,
}) => {
   const intl = useIntl();

   function isOnlyTimeString(str) {
      return moment(str, "HH:mm", true).isValid() || moment(str, "HH:mm:ss", true).isValid();
   }

   const checkDate = (date: Date | Moment | String | null | undefined) => {
      if (isMoment(date) && date.isValid()) {
         return date.toDate();
      } else if (isDate(date) && !isNaN(date.getTime())) {
         return date;
      } else if (typeof date === "string") {
         if (isOnlyTimeString(date)) {
            return moment(date, ["HH:mm", "HH:mm:ss"]).toDate();
         }
         return moment(date).toDate();
      } else {
         return null;
      }
   };

   const [selectedDate, setSelectedDate] = useState<Date | Moment | null>(checkDate(defaultValue));
   const [showYearPicker, setShowYearPicker] = useState(false);
   const [showMonthPicker, setShowMonthPicker] = useState(false);

   const handleDateChange = (date: Date) => {
      setSelectedDate(date);
      if (onChange) {
         if (date) {
            onChange(moment(date));
         } else {
            onChange("");
         }
      }

      if (showYearPicker) {
         setShowYearPicker(false);
         setShowMonthPicker(true);
      }
      if (showMonthPicker) {
         setShowMonthPicker(false);
      }
   };

   const getCalendarClassName = (size: string | undefined) => {
      switch (size) {
         case "small":
            return "small-calendar";
         case "large":
            return "large-calendar";
         default:
            return `medium-calendar`;
      }
   };

   return (
      <div className={`bp2datepicker ${className}`} style={style}>
         <ReactDatePicker
            wrapperClassName={wrapperClassName}
            portalId="calendar-root"
            locale={"pl-PL"}
            showIcon
            open={isOpen}
            inline={inline}
            // @ts-ignore
            icon={value || selectedDate ? <span /> : icon ? icon : <Icon_Calendar stroke="none" />}
            selected={checkDate(value || selectedDate)}
            onCalendarOpen={() => {
               setShowYearPicker(false);
               setShowMonthPicker(false);
               onOpenChange && onOpenChange();
            }}
            onInputClick={onStartEdit}
            onCalendarClose={onOpenChange && onOpenChange}
            onClickOutside={onClickOutside && onClickOutside}
            onChange={handleDateChange}
            disabled={disabled}
            readOnly={readOnly}
            showTimeSelect={showTime}
            showTimeSelectOnly={showTimeOnly}
            timeIntervals={timeIntervals ?? 10}
            minTime={minTime && maxTime && minTime}
            maxTime={minTime && maxTime && maxTime}
            minDate={minDate}
            maxDate={maxDate}
            dateFormat={format || (showTime ? "yyyy-MM-dd HH:mm" : "yyyy-MM-dd")}
            placeholderText={
               placeholder || showTimeOnly
                  ? intl.formatMessage({ id: "timepicker.placeholder" })
                  : intl.formatMessage({ id: "datepicker.placeholder" })
            }
            className={className + getCalendarClassName(size)}
            calendarClassName={`bp2datepicker-popover`}
            todayButton={intl.formatMessage({ id: "datepicker.today" })}
            isClearable
            showYearPicker={showYearPicker}
            showMonthYearPicker={showMonthPicker}
            shouldCloseOnSelect={!showYearPicker && !showMonthPicker}
            renderCustomHeader={({ date, changeYear, changeMonth }) => (
               <CustomHeader
                  date={date}
                  changeYear={changeYear}
                  changeMonth={changeMonth}
                  showYearPicker={showYearPicker}
                  showMonthPicker={showMonthPicker}
                  setShowYearPicker={setShowYearPicker}
                  setShowMonthPicker={setShowMonthPicker}
               />
            )}
         />
      </div>
   );
};
